import React, { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
// make sure to import debounce from the dedicated submodule:
import debounce from "lodash/debounce";

export default function LiveSearchCombo({
  value,
  placeholder = "Search…",
  onSelect,
  // note: searchFn(query, { signal }) → Promise<…>
  searchFn,
}) {
  const [q, setQ] = useState(value?.label || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // 1) create a real ref to hold our AbortController
  const abortRef = useRef(null);

  // 2) wrap your actual fetch logic in a debounced, stable callback
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      // cancel any previous request
      abortRef.current?.abort();

      // create a new controller for this run
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const res = await searchFn(query, { signal: controller.signal });
        setItems(
          res.map((i) => ({
            id: i.id,
            label: i.fullName,
            sub: i.username,
            avatar: i.avatar ?? null,
          }))
        );
      } catch (err) {
        // only suppress the abort; log everything else
        if (err.name !== "AbortError" && err.name !== "CanceledError") {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    }, 300),
    [searchFn]
  );

  // 3) kick off (or cancel) whenever `q` changes
  useEffect(() => {
    if (q.trim()) {
      debouncedSearch(q);
    } else {
      // on empty query, cancel & clear
      abortRef.current?.abort();
      setItems([]);
      setLoading(false);
    }
    return () => {
      // clean up on unmount or before next effect
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [q, debouncedSearch]);

  const handleChange = (e) => {
    setQ(e.target.value);
  };

  return (
    <div className="relative">
      <Input value={q} placeholder={placeholder} onChange={handleChange} />
      {loading && (
        <Loader2 className="absolute top-1/2 right-2 h-4 w-4 animate-spin text-muted-foreground" />
      )}

      <AnimatePresence>
        {items.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg overflow-auto max-h-60"
          >
            {items.map((it) => (
              <li
                key={it.id}
                onMouseDown={() => {
                  onSelect(it);
                  setItems([]);
                  setQ(it.label);
                }}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted cursor-pointer"
              >
                {it.avatar && (
                  <img
                    src={it.avatar}
                    className="h-6 w-6 rounded-full object-cover"
                  />
                )}
                <span className="text-sm">{it.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {it.sub}
                </span>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
